import cx from "classnames";
import { CSSProperties, FC, RefObject, ReactNode, useContext, useEffect, useId, useMemo, useRef, useState, JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import FlyoutContextProvider, { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Portal from "@app/shared/Portal";
import { debounce } from "lodash";

interface NewActionMenuProps {
    /**
     * Aligns the dropdown to the right side of the button instead of left
     * Useful for when the button is on the right edge of the page
     */
    alignRight?: boolean;
    /**
     * Optional classnames applied to the button. Defaults to "h-100"
     */
    buttonClassNames?: string;
    /**
     * An optional element to show in the action menu trigger.
     * This will override the buttonText.
     */
    buttonElement?: JSX.Element;
    /**
     * The text displayed within the button. This can be updated by the parent component,
     * typically to reflect selections.
     * This will be overridden by buttonElement, if provided.
     */
    buttonText: string | JSX.Element;
    /**
     * An option to close the menu on any click
     */
    closeOnClick?: boolean;
    /**
    * Class names to apply to the checkbox-dropdown-container div, the outermost div for this component
    */
    containerClassnames?: string;
    /**
     * Boolean to disable the primary button and all checkboxes in the open dropdown,
     * as well as hide the cancel and submit buttons in the dropdown
     */
    disabled?: boolean;
    /**
     * Causes the dropdown to appear above the button instead of below
     */
    dropup?: boolean;
    /**
     * An element ref used to dynamically determine if dropup should be true.
     */
    dropupContainer?: React.RefObject<HTMLElement>;
    /**
     * Renders the menu with position: fixed to get around overflow restrictions
     * Its location will be based on the cursor at the time of the click, 
     * and it will be removed on scroll because otherwise it'd get weird
     */
    fixMenu?: boolean;
    /**
     * An extra optional offset for the position of the fixed action menu.
     * The x value moves it right as it increases, while the y value moves it down.
     */
    fixedMenuOffset?: { x: number, y: number };
    /**
     * Hides the arrow icon that typically denotes a dropdown/dropup menu.
     * This will probably be used in cases where the trigger is already another icon.
     */
    hideArrow?: boolean;
    /**
     * Disables the primary button and (when showSpinner is enabled) appends a loading spinner to the buttonText
     */
    loading?: boolean;
    /**
     * Disables the max-height for this element
     */
    disableMaxHeight?: boolean;
    /**
     * Appends a loading spinner to the buttonText when loading is true
     */
    showSpinner?: boolean;
    /**
     * Used for automated testing
     */
    testId: string;
    /**
     * A ref to override any triggerElement descendents from this component.
     * This is useful if the ref needs to be passed into a sibling component for any reason.
     * This does NOT override the trigger for the actionMenuContext, only for 
     * a flyout triggered from within the action menu.
     */
    triggerElementOverride?: RefObject<HTMLButtonElement>;
}

/**
 * A toggle button for a div containing any children. <br /><br />
 * Docs note: The showSpinner prop currently does not work in Storybook because the spinner relies on Bootstrap/Looper styles.<br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const WrappedNewActionMenu = (props: NewActionMenuProps & { children?: ReactNode }): JSX.Element => {
    const actionMenuContext = useContext(FlyoutContext);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [fixedPositioning, setFixedPositioning] = useState<CSSProperties>({});
    const uniqueId = useId();

    useEffect(() => {
        actionMenuContext.setTriggerElement(triggerRef);
    }, [triggerRef.current]);

    useEffect(() => {
        if (actionMenuContext.isFlyoutVisible) {
            const handleExternalClick = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (target.closest(`[data-action-menu-id="${CSS.escape(uniqueId)}"], #${CSS.escape(uniqueId)}.checkbox-dropdown-container`) === null && actionMenuContext.isFlyoutVisible) {
                    actionMenuContext.handleCloseFlyout();
                    document.body.removeEventListener("click", handleExternalClick);
                }
            };

            document.body.addEventListener("click", handleExternalClick);

            return () => {
                document.body.removeEventListener("click", handleExternalClick);
            };
        }
    }, [actionMenuContext.isFlyoutVisible]);

    useEffect(() => {
        if (props.closeOnClick && actionMenuContext.isFlyoutVisible && actionMenuContext.flyoutRef?.current) {
            const handleInternalClick = () => {
                actionMenuContext.handleCloseFlyout();
            }
            actionMenuContext.flyoutRef.current.addEventListener("click", handleInternalClick);

            return () => {
                actionMenuContext.flyoutRef?.current?.removeEventListener("click", handleInternalClick);
            }
        }
    }, [actionMenuContext.isFlyoutVisible, props.closeOnClick]);

    useEffect(() => {
        const handleResize = debounce(() => {
            updateFixedPositioning();
        }, 150);

        if (props.fixMenu) {
            window.addEventListener("resize", handleResize);
        }

        return () => {
            // removeEventListener doesn't complain if it doesn't find a matching event
            window.removeEventListener("resize", handleResize);
            // Without this, a resize already in the 150ms debounce window still fires after
            // unmount (e.g. this row scrolled out of a virtualized list), crashing on a null ref.
            handleResize.cancel();
        }
    }, []);

    useEffect(() => {
        if (props.fixMenu && actionMenuContext.isFlyoutVisible) {
            window.addEventListener("scroll", actionMenuContext.handleCloseFlyout);
            // scroll events don't bubble, so window's listener never fires for scrolling inside a
            // nested overflow container (e.g. a table) — listen on dropupContainer directly too.
            props.dropupContainer?.current?.addEventListener("scroll", actionMenuContext.handleCloseFlyout);
        }

        return () => {
            window.removeEventListener("scroll", actionMenuContext.handleCloseFlyout);
            props.dropupContainer?.current?.removeEventListener("scroll", actionMenuContext.handleCloseFlyout);
        }
    }, [actionMenuContext.isFlyoutVisible, props.fixMenu, props.dropupContainer]);

    const updateFixedPositioning = () => {
        if (!props.fixMenu || !triggerRef.current) {
            setFixedPositioning({});
            return;
        }

        // dropupContainer's own bottom edge can be well above window.innerHeight (e.g. a scrollable
        // table with page content below it), so use whichever boundary is more restrictive.
        const containerBottom = props.dropupContainer?.current?.getBoundingClientRect().bottom;
        const windowBottom = containerBottom !== undefined ? Math.min(window.innerHeight, containerBottom) : window.innerHeight;
        const triggerBox = triggerRef.current.getBoundingClientRect();
        const buttonTop = triggerBox.top;
        //menuHeight is also an assumption taken from the project's options dropdown
        const menuHeight = 223;

        if (buttonTop + menuHeight > windowBottom) {
            const x = triggerBox.x + triggerBox.width;
            const y = triggerBox.bottom - menuHeight;
            setFixedPositioning({
                left: `${x + (props.fixedMenuOffset?.x || 0)}px`,
                top: `${y + (props.fixedMenuOffset?.y || 0)}px`,
            });

            return;
        }

        const x = triggerBox.x + triggerBox.width;
        const y = triggerBox.y;
        setFixedPositioning({
            left: `${x + (props.fixedMenuOffset?.x || 0)}px`,
            top: `${y + (props.fixedMenuOffset?.y || 0)}px`,
        });
    }

    const renderSpinner = () => (
        <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    const dropupWhenAppropriate = useMemo(() => {
        // fixMenu positions the dropdown entirely via inline top/left (see updateFixedPositioning);
        // applying the .dropup class on top of that would add a CSS bottom the inline style never
        // overrides, stretching the fixed element between the two.
        if (!props.fixMenu && !!props.dropupContainer && !!props.dropupContainer.current && actionMenuContext.isFlyoutVisible) {
            const windowBottom = window.innerHeight;
            const buttonTop = triggerRef.current.getBoundingClientRect().top;
            // the 250 value is arbitrary, but based on the service area editor's county status dropdown
            const arbitraryDistanceFromBottom = 250;
            if (buttonTop + arbitraryDistanceFromBottom > windowBottom ||
                buttonTop + arbitraryDistanceFromBottom > props.dropupContainer.current.getBoundingClientRect().bottom) {
                return true;
            }
            return false;
        }
        return false;
    }, [props.dropupContainer, actionMenuContext.isFlyoutVisible, props.fixMenu])

    const dropdown = (
        <div
            className={cx({
                "align-right": props.alignRight,
                "checkbox-dropdown": true,
                "dropup": props.dropup || dropupWhenAppropriate,
                "fixed": props.fixMenu,
                "hidden": !actionMenuContext.isFlyoutVisible,
                "no-max-height": props.disableMaxHeight,
            })}
            aria-hidden={!actionMenuContext.isFlyoutVisible}
            // clicks inside the dropdown were bubbling up and triggering the button's setIsVisible multiple times
            onClick={(e) => { e.stopPropagation(); }}
            ref={actionMenuContext.flyoutRef}
            style={fixedPositioning}
        >
            <div ref={actionMenuContext.flyoutBodyRef}>
                {props.children}
            </div>
        </div>
    );

    const containerClassName = cx({
        [props.containerClassnames]: !!props.containerClassnames,
        "checkbox-dropdown-container": true,
    });

    return (
        <div
            className={containerClassName}
            id={uniqueId}
        >
            <QESButton
                qesTestId={`new-action-menu__${props.testId}-button`}
                qesType="none"
                className={`${props.buttonClassNames || "h-100"} new-action-menu__button`}
                onClick={() => {
                    actionMenuContext.isFlyoutVisible ? actionMenuContext.handleCloseFlyout() : actionMenuContext.handleOpenMenu();
                    if (props.fixMenu) {
                        updateFixedPositioning();
                    }
                }}
                disabled={props.loading || props.disabled}
                aria-pressed={actionMenuContext.isFlyoutVisible}
                qesRef={(el) => {
                    triggerRef.current = el;
                    if (!!props.triggerElementOverride) {
                        props.triggerElementOverride.current = el;
                    }
                }}
            >
                <>
                    {props.buttonElement || props.buttonText}
                    {props.loading && props.showSpinner ?
                        renderSpinner() :
                        props.hideArrow ?
                            null :
                            <span className="ms-1" aria-hidden="true"><QesIcon iconName={props.dropup ? QesIcons.ChevronUp : QesIcons.ChevronDown} description="Expand" /></span>
                    }
                </>
            </QESButton>
            {
                // A dropupContainer means the caller is telling us this menu lives inside a scrollable
                // ancestor. That ancestor's own native scrollbar paints on top of ANY descendant content
                // that overlaps its screen region, position: fixed included, since the descendant is
                // still nested inside the scrolling box for compositing purposes even though fixed
                // positioning already escapes its layout/overflow clipping. Portaling to document.body
                // makes the dropdown a true sibling instead, so it's no longer subject to that box's
                // scrollbar paint order.
                //
                // Consumers commonly style the dropdown via containerClassnames-scoped descendant
                // selectors (e.g. ".project-action-menu-container .checkbox-dropdown"), which only match
                // while the dropdown is a DOM descendant of that container. Re-wrapping it in a div
                // carrying the same class once portaled keeps every such rule matching.
                props.fixMenu && props.dropupContainer
                    // Uses a data attribute (not id) to avoid duplicating uniqueId onto two DOM
                    // elements — the outside-click handler above matches on
                    // `[data-action-menu-id="${uniqueId}"]` for this portaled copy.
                    ? <Portal hideBodyScroll={false}><div data-action-menu-id={uniqueId} className={containerClassName}>{dropdown}</div></Portal>
                    : dropdown
            }
        </div>
    );
};

const NewActionMenu: FC<NewActionMenuProps & { children?: ReactNode }> = (props: NewActionMenuProps & { children?: ReactNode }) => {
    return (
        <FlyoutContextProvider>
            <WrappedNewActionMenu {...props}>
                {props.children}
            </WrappedNewActionMenu>
        </FlyoutContextProvider>
    );
}

export default NewActionMenu;
